import { GeoPoint } from "firebase/firestore";

import { FirebaseDocType } from "@/functions/src/models/firebase";

import {
  AggregateMaterialEnum,
  BinMaterialEnum,
  BinSizeEnum,
  BinStatusEnum,
  BinTaskEnum,
  CleanupTypeEnum,
  ContactType,
  CreatedByType,
  CustomerType,
  JunkSizeEnum,
  LocationAccessEnum,
  SchedulePreferenceEnum,
  ServiceTaskTypeEnum,
  ServiceTypeEnum,
  TaskDriverStatusEnum,
} from "./common";
import { FirebaseDateType } from "./firebase";

type JobAssignedBinType = {
  assignedAt: FirebaseDateType;
  createdBy: CreatedByType; // This points to the CreatedBy object but should be a ref to a user's collection
  deleted: boolean;
  deletedAt?: FirebaseDateType | null;
  id?: string; // This is the same id as bin's id in bins collection but should be a ref
  identifier: string; // This is likely the bin's name. Eg: 20Y-030, 10Y-002
  size: BinSizeEnum;
  status: BinStatusEnum;
};

type JobAssignedByType = {
  name: string;
  email: string;
  uid: string; // User's collection id
};

type JobLastUpdatedByType = {
  displayName: string;
  email: string;
  uid: string; // User's collection id
};

export type PhotoWithCommentType = {
  photoUrl: string; // A path to a photo in a bucket
  comment: string;
};

export enum JunkLoadStatusEnum {
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
}

export type JunkLoadType = {
  preJunkRemovalPhoto?: PhotoWithCommentType | string | null;
  loadSize?: JunkSizeEnum | null;
  locationAccess?: LocationAccessEnum | null;
  postJunkRemovalPhoto?: PhotoWithCommentType | string | null;
  weightSurcharge?: string | null;
  dumpReceiptPhotoUrl?: PhotoWithCommentType | string | null; // A path to a photo in a bucket
  loadNumber: number;
  status: JunkLoadStatusEnum;
};

export type JobConnectedTaskSummaryType = {
  address: string;
  adminNotes: string;
  assignedBin: JobAssignedBinType | null; // This points to JobAssignedBin object but should be a bin's ref
  assignedBy: JobAssignedByType | null; // This points to the JobAssignedBy object but should be a ref to a user's collection
  assignedTo?: string | null; // This is the same id as user's id in users collection but should be a ref
  billed: boolean;
  binTask: BinTaskEnum | null;
  completed: boolean;
  contact: ContactType;
  createdAt: FirebaseDateType;
  createdBy: CreatedByType; // This points to the CreatedBy object but should be a ref to a user's collection
  customer: CustomerType; // This points to Customer object but should be a customer's ref
  date: FirebaseDateType;
  deleted: boolean;
  driver?: string | null; // A simple driver's name
  dumpReceiptPhotoUrl?: PhotoWithCommentType | string | null; // A path to a photo in a bucket
  dueAt: FirebaseDateType | null;
  end: FirebaseDateType | null;
  floorSqftSiteLocked: boolean;
  floorsCleanedSiteLocked: boolean;
  floorsCleaned?: string | number;
  weightSurcharge?: string;
  floorSqft?: string;
  geopoint: GeoPoint;
  jobId: string; // This is the same id as job's id in jobs collection but should be a ref
  id: string; // This is the same id as upper jobId service sub-collection id but should be a ref
  lastUpdatedBy: JobLastUpdatedByType | null; // This points to the JobLastUpdatedBy object but should be a ref to a user's collection
  notes: string;
  schedulePreference: SchedulePreferenceEnum;
  size: BinSizeEnum | null;
  stage: number | null;
  started: boolean;
  startedUpdatedAt?: FirebaseDateType;
  completedUpdatedAt?: FirebaseDateType;
  cleaningStartedAt?: FirebaseDateType;
  taskNotes: string;
  type: ServiceTypeEnum;
  driverFlowStatus?: TaskDriverStatusEnum;
  needToDump?: boolean;
  hasDumped?: boolean;
  dumpedAt?: FirebaseDateType;
  preJunkRemovalPhoto?: PhotoWithCommentType | string | null;
  postJunkRemovalPhoto?: PhotoWithCommentType | string | null;
  binMaterials?: BinMaterialEnum;
  siteDropPhoto?: PhotoWithCommentType | string | null;
  binMaterialsPhoto?: PhotoWithCommentType | string | null;
  turnaroundPhoto?: PhotoWithCommentType | null;
  sqft?: number | null;
  cleanedAreasPhotos?: PhotoWithCommentType[] | string[];
  junkLoads?: JunkLoadType[];
};

export type JobConnectedTaskType = JobConnectedTaskSummaryType & {
  connectedTask: JobConnectedTaskSummaryType | null;
};

type JobSiteType = {
  address: string;
  contact: ContactType;
  customer: CustomerType; // This points to Customer object but should be a customer's ref
  floorsCleaned: string;
  geopoint: GeoPoint;
  id: string; // This is the same id as sites' id in sites collection but should be a ref
  lockbox: string;
  notes: string;
};

export type AggregateType = {
  material: AggregateMaterialEnum;
  unitPrice: number;
  yardAmount: number;
};

// MAIN COLLECTION //

export type JobType = FirebaseDocType & {
  address?: string;
  assignedTo?: string | null; // This is the same id as user's id in users collection but should be a ref
  billed?: boolean;
  binTask?: BinTaskEnum | null;
  cleanupType?: CleanupTypeEnum | null;
  completed: boolean;
  completedAt: FirebaseDateType | null;
  connectedTask?: JobConnectedTaskType | null;
  contact?: ContactType;
  createdAt: FirebaseDateType;
  createdBy: CreatedByType; // This points to the CreatedBy object but should be a ref to a user's collection
  customer?: CustomerType; // This points to Customer object but should be a customer's ref
  date: FirebaseDateType;
  dueAt?: FirebaseDateType;
  end?: FirebaseDateType;
  geopoint: GeoPoint;
  hasBin: boolean;
  hasJunk: boolean;
  site: JobSiteType | null; // This points to JobSite object but should be a site's ref
  size: BinSizeEnum | null;
  aggregate: AggregateType | null;
  startDate: FirebaseDateType;
  started: boolean;
  toReview?: boolean;
  binOverdueDays?: number;
};

// SUB-COLLECTIONS //

// Job collection has Services sub-collection with the same structure as JobConnectedTask plus the following
export type ServicesType = JobConnectedTaskType & {
  loadSize: JunkSizeEnum | null;
  extraLoadSize: JunkSizeEnum;
  locationAccess: LocationAccessEnum | null;
};

// Job collection has Notes sub-collection with the same structure as JobConnectedTask
export type NotesType = {
  createdAt: FirebaseDateType;
  createdBy: NotesCreatedByType; // This points to the NotesCreatedBy object but should be a ref to a user's collection
  files: string[]; // Couldn't find the type of files
  jobId: string; // This is the same id as parent job's id
  note: string;
};

type NotesCreatedByType = {
  id: string; // This is the same id as user's id in users collection but should be a ref
  name: string;
  photoURL: string;
};

// Job collection has Tasks sub-collection
type TaskSummaryType = {
  address: string;
  assignedTo: string | null; // This is the same id as user's id in users collection but should be a ref
  completed: boolean;
  completedUpdatedAt: FirebaseDateType;
  assignedBin?: JobAssignedBinType | null; // This points to JobAssignedBin object but should be a bin's ref
  dueAt: FirebaseDateType;
  cleanupType?: CleanupTypeEnum | null;
  end: FirebaseDateType;
  geopoint: GeoPoint;
  jobId: string; // This is the same id as parent job's id
  serviceId: string; //  This is the same id as parent services' id
  size: BinSizeEnum;
  started: boolean;
  startedUpdatedAt: FirebaseDateType;
  cleaningStartedAt?: FirebaseDateType;
  taskId: ServiceTaskTypeEnum;
  driverFlowStatus?: TaskDriverStatusEnum;
  needToDump?: boolean;
  hasDumped?: boolean;
  dumpedAt?: FirebaseDateType;
};

export type TasksType = TaskSummaryType & {
  connectedTask?: TaskSummaryType;
};
